import React, { useCallback, useEffect, useRef, useState } from "react";
import config from "../config.js";
import ErrorBoundary from "../components/ErrorBoundary";
import {
  themeFor, MEDALS, slideDuration, nextSlideIndex, clampSlideIndex, mainBundleName, isNewBuild,
  gains, barPercent, formatClock, formatDay, formatNumber,
} from "../utils/shopTv";

/**
 * ShopTvDisplay — the wall-TV page at /tv/<token> (Raspberry Pi, Chromium kiosk).
 *
 * Rendered OUTSIDE the site login and the app chrome, like the customer portals:
 * the 256-bit token in the URL is the only credential, and the API side
 * (GET /portal/display/{token}) returns only this TV's slides — operator short
 * names and counts. What the TV shows is edited server-side (/displays), so the
 * Pi is never touched to change it.
 *
 * Unattended rules: keep showing the last good data when JobQ can't be reached
 * (with an "offline" note), never reload while offline, and reload only when a
 * NEW build is deployed (the main bundle index.html names differs from the one
 * this page loaded).
 */
const BUILD_CHECK_MS = 5 * 60 * 1000;
const HIGHLIGHT_MS = 9000;
const CRASH_RELOAD_MS = 60 * 1000;

function loadedBundle() {
  return mainBundleName(Array.from(document.scripts || []).map((s) => s.src).join(" "));
}

/**
 * Nobody is at the TV to press "Try again": a render crash reloads the page a
 * minute later (by then a fixed deploy or JobQ is usually back).
 */
function CrashReload() {
  useEffect(() => {
    const t = setTimeout(() => window.location.reload(), CRASH_RELOAD_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ position: "fixed", inset: 0, background: "#0b1220", color: "#94a3b8", display: "flex",
      alignItems: "center", justifyContent: "center", fontFamily: "system-ui, sans-serif", fontSize: "3vh" }}>
      Restarting the board…
    </div>
  );
}

export default function ShopTvDisplay() {
  return (
    <ErrorBoundary fallback={<CrashReload />}>
      <ShopTvBoard />
    </ErrorBoundary>
  );
}

function ShopTvBoard() {
  const API_BASE = config.API_BASE_URL;
  // Tokens are URL-safe base64: taken as-is (decoding a stray "%" would throw).
  const token = (window.location.pathname.split("/tv/")[1] || "").split("/")[0] || "";

  const [payload, setPayload] = useState(null);
  const [state, setState] = useState("loading");   // loading | ok | offline | missing
  const [lastOkAt, setLastOkAt] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [highlights, setHighlights] = useState({});
  const prevRowsRef = useRef({});
  const bundleRef = useRef(loadedBundle());
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (!token) { setState("missing"); return; }
    const seq = ++seqRef.current;
    try {
      // A URL object, not a string, on purpose: config.js's fetch wrapper adds the
      // API key only to string URLs. The TV relies on its token alone, and a plain
      // GET with no custom header needs no CORS preflight.
      const res = await fetch(new URL(`${API_BASE}/portal/display/${encodeURIComponent(token)}`), { cache: "no-store" });
      if (seq !== seqRef.current) return;                 // a newer poll already answered
      if (res.status === 404) {
        setPayload(null);
        prevRowsRef.current = {};
        setState("missing");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (seq !== seqRef.current) return;
      const hl = {};
      for (const s of body.slides || []) {
        if (s.type !== "leaderboard" || !s.data) continue;
        const up = gains(prevRowsRef.current[s.key], s.data.rows);
        for (const [id, n] of Object.entries(up)) hl[`${s.key}:${id}`] = n;
        prevRowsRef.current[s.key] = s.data.rows;
      }
      if (Object.keys(hl).length) setHighlights(hl);
      setPayload(body);
      setState("ok");
      setLastOkAt(new Date());
    } catch (e) {
      if (seq === seqRef.current) setState("offline");
    }
  }, [API_BASE, token]);

  const refreshSec = payload?.display?.refresh_seconds || 60;
  const slides = payload?.slides || [];
  const count = slides.length;
  const idx = clampSlideIndex(slideIdx, count);
  const current = slides[idx] || null;
  const currentKey = current?.key;
  const dwell = slideDuration(current);

  useEffect(() => { document.title = "JobQ TV"; }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, refreshSec * 1000);
    return () => clearInterval(t);
  }, [load, refreshSec]);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!Object.keys(highlights).length) return undefined;
    const t = setTimeout(() => setHighlights({}), HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [highlights]);
  useEffect(() => {
    if (count <= 1) return undefined;
    const t = setTimeout(() => setSlideIdx((i) => nextSlideIndex(clampSlideIndex(i, count), count)), dwell * 1000);
    return () => clearTimeout(t);
  }, [idx, count, currentKey, dwell]);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const res = await fetch(`/index.html?build=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const html = await res.text();
        if (!isNewBuild(bundleRef.current, html)) return;
        // Only reload once the new bundle itself downloads: a Wi-Fi drop mid-reload
        // would otherwise leave a blank page with no script left to recover it.
        const bundle = await fetch(`/static/js/${mainBundleName(html)}`, { cache: "no-store" });
        if (bundle.ok) window.location.reload();
      } catch (e) { /* offline: keep showing what we have */ }
    }, BUILD_CHECK_MS);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    // Belt and braces: the Pi turns screen blanking off, a wake lock asks the
    // browser too. Unsupported or refused is fine.
    let lock = null;
    const take = async () => {
      try { lock = await navigator.wakeLock?.request?.("screen"); } catch (e) { lock = null; }
    };
    take();
    const again = () => { if (document.visibilityState === "visible") take(); };
    document.addEventListener("visibilitychange", again);
    return () => { document.removeEventListener("visibilitychange", again); try { lock?.release?.(); } catch (e) { /* ignore */ } };
  }, []);

  const theme = themeFor(payload?.display?.theme_resolved);
  const vars = {
    "--tv-bg1": theme.bg1, "--tv-bg2": theme.bg2, "--tv-panel": theme.panel, "--tv-line": theme.line,
    "--tv-text": theme.text, "--tv-muted": theme.muted, "--tv-accent": theme.accent,
    "--tv-accent2": theme.accent2, "--tv-bar": theme.bar,
  };
  const decor = theme.decor || [];

  let body;
  if (state === "missing") {
    body = (
      <div className="jqtv-empty">
        <div className="jqtv-empty-big">This TV isn't connected to a JobQ display</div>
        <div>Ask the office to check its Shop TV link.</div>
      </div>
    );
  } else if (!payload) {
    body = (
      <div className="jqtv-empty">
        <div className="jqtv-empty-big">{state === "offline" ? "Can't reach JobQ right now" : "Loading…"}</div>
        {state === "offline" ? <div>Trying again every minute.</div> : null}
      </div>
    );
  } else if (!current) {
    body = (
      <div className="jqtv-empty">
        <div className="jqtv-empty-big">Nothing is scheduled for this TV</div>
      </div>
    );
  } else if (current.type === "leaderboard") {
    body = <Leaderboard slide={current} highlights={highlights} />;
  } else {
    body = <MessageSlide slide={current} fallbackEmoji={decor[0]} />;
  }

  const data = current?.type === "leaderboard" ? current.data : null;
  const unit = current?.unit_label || "parts";

  return (
    <div className="jqtv" style={vars}>
      <style>{CSS}</style>
      {decor.length ? <div className="jqtv-watermark" aria-hidden="true">{decor[decor.length - 1]}</div> : null}
      <header className="jqtv-head">
        <div className="jqtv-titles">
          <div className="jqtv-eyebrow">KanCraft MFG</div>
          <div className="jqtv-title">
            {decor[0] ? <span className="jqtv-decor" aria-hidden="true">{decor[0]}</span> : null}
            {current?.title || payload?.display?.name || "JobQ"}
          </div>
          {current?.subtitle ? <div className="jqtv-sub">{current.subtitle}</div> : null}
        </div>
        <div className="jqtv-clock">
          <div className="jqtv-time">{formatClock(now)}</div>
          <div className="jqtv-day">{formatDay(now)}</div>
        </div>
      </header>
      <main className="jqtv-main">{body}</main>
      <footer className="jqtv-foot">
        <div className="jqtv-foot-left">
          {data ? (
            <>
              <strong>{formatNumber(data.total)}</strong> {unit} · {data.operators} operator{data.operators === 1 ? "" : "s"}
              {data.today_total > 0 ? <> · <strong className="jqtv-accent">{formatNumber(data.today_total)}</strong> today</> : null}
            </>
          ) : null}
        </div>
        <div className="jqtv-dots">
          {count > 1 ? slides.map((s, i) => <span key={s.key || i} className={i === idx ? "on" : ""} />) : null}
        </div>
        <div className="jqtv-foot-right">
          {state === "offline" && lastOkAt
            ? <span className="jqtv-offline">● Offline — showing {formatClock(lastOkAt)}</span>
            : lastOkAt ? <span>Updated {formatClock(lastOkAt)}</span> : null}
        </div>
      </footer>
    </div>
  );
}

function Leaderboard({ slide, highlights }) {
  const board = <LeaderboardRows slide={slide} highlights={highlights} />;
  if (!slide.ready) return board;
  return (
    <div className="jqtv-split">
      <div className="jqtv-split-main">{board}</div>
      <ReadyPanel slide={slide} />
    </div>
  );
}

/** What is sitting at the station with its upstream work done (for the Brake: cut and waiting to bend). */
function ReadyPanel({ slide }) {
  const r = slide.ready;
  const unit = slide.unit_label || "parts";
  return (
    <aside className="jqtv-ready" aria-label={`Ready at the ${slide.station}`}>
      <div className="jqtv-ready-eyebrow">Ready at the {slide.station}</div>
      <div className="jqtv-ready-num">{formatNumber(r.pieces)}</div>
      <div className="jqtv-ready-sub">
        {r.pieces > 0 ? <>{unit} waiting · {r.jobs} job{r.jobs === 1 ? "" : "s"}</> : <>Queue is clear</>}
      </div>
      {r.models && r.models.length ? (
        <div className="jqtv-ready-models">
          {r.models.map((m) => (
            <div key={m.label} className="jqtv-ready-model">
              <span>{m.label}</span>
              <strong>{formatNumber(m.pieces)}</strong>
            </div>
          ))}
        </div>
      ) : null}
    </aside>
  );
}

function LeaderboardRows({ slide, highlights }) {
  const data = slide.data;
  if (!data) {
    return (
      <div className="jqtv-empty">
        <div className="jqtv-empty-big">{slide.error || "This board could not be loaded right now."}</div>
        <div>Trying again shortly.</div>
      </div>
    );
  }
  const rows = data.rows || [];
  const unit = slide.unit_label || "parts";
  if (!rows.length) {
    return (
      <div className="jqtv-empty">
        <div className="jqtv-empty-big">No {unit} yet</div>
        <div>The first one takes the lead!</div>
      </div>
    );
  }
  const leader = rows[0].count;
  return (
    <div className="jqtv-board" style={{ "--rows": rows.length }}>
      {rows.map((r) => {
        const up = highlights[`${slide.key}:${r.id}`];
        return (
          <div key={r.id ?? r.name} data-testid={`tv-row-${r.id}`} className={`jqtv-row${r.rank <= 3 ? ` top top-${r.rank}` : ""}${up ? " is-up" : ""}`}>
            <div className="jqtv-rank">{MEDALS[r.rank] || r.rank}</div>
            <div className="jqtv-name">{r.name}</div>
            <div className="jqtv-barwrap"><div className="jqtv-bar" style={{ width: `${barPercent(r.count, leader)}%` }} /></div>
            <div className="jqtv-today">{r.today > 0 ? `+${formatNumber(r.today)} today` : ""}</div>
            <div className="jqtv-count">{formatNumber(r.count)}</div>
            {up ? <div className="jqtv-pop">+{formatNumber(up)}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

function MessageSlide({ slide, fallbackEmoji }) {
  const emoji = slide.emoji || fallbackEmoji;
  return (
    <div className="jqtv-msg">
      {emoji ? <div className="jqtv-msg-emoji" aria-hidden="true">{emoji}</div> : null}
      {slide.body ? <div className="jqtv-msg-body">{slide.body}</div> : null}
    </div>
  );
}

// Sized in viewport units so the same page fills a 1080p or a 4K TV. vh drives
// row height (10 rows must fit), vw caps text so a narrow window still works.
const CSS = `
.jqtv { position: fixed; inset: 0; overflow: hidden; cursor: none; user-select: none;
  background: linear-gradient(135deg, var(--tv-bg1), var(--tv-bg2)); color: var(--tv-text);
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans", "DejaVu Sans", sans-serif;
  display: flex; flex-direction: column; padding: 3vh 3.2vw 2.2vh; box-sizing: border-box; }
.jqtv *, .jqtv *::before, .jqtv *::after { box-sizing: border-box; }
.jqtv-watermark { position: absolute; right: -3vw; bottom: -9vh; font-size: 40vh; opacity: 0.035; pointer-events: none; line-height: 1; }
.jqtv-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 2vw;
  padding-bottom: 1.8vh; border-bottom: 0.35vh solid var(--tv-line); }
.jqtv-eyebrow { font-size: min(2vh, 1.1vw); letter-spacing: 0.4em; text-transform: uppercase; color: var(--tv-muted); font-weight: 700; }
.jqtv-title { font-size: min(8.2vh, 4.6vw); font-weight: 900; line-height: 1.05; letter-spacing: -0.01em; }
.jqtv-decor { margin-right: 0.35em; }
.jqtv-sub { font-size: min(3.4vh, 1.9vw); color: var(--tv-accent); font-weight: 700; margin-top: 0.4vh; }
.jqtv-clock { text-align: right; white-space: nowrap; }
.jqtv-time { font-size: min(6.4vh, 3.6vw); font-weight: 800; font-variant-numeric: tabular-nums; line-height: 1; }
.jqtv-day { font-size: min(2.6vh, 1.5vw); color: var(--tv-muted); margin-top: 0.8vh; }
.jqtv-main { flex: 1; min-height: 0; display: flex; flex-direction: column; justify-content: center; padding: 2.4vh 0 1.6vh; }
.jqtv-board { height: 100%; display: flex; flex-direction: column; gap: 1.2vh; }
.jqtv-row { position: relative; flex: 0 0 auto;
  height: min(10.5vh, calc((100% - (var(--rows) - 1) * 1.2vh) / var(--rows)));
  display: grid; grid-template-columns: 9% minmax(0, 30%) 1fr 15% 13%; align-items: center; gap: 1.4vw;
  padding: 0 2vw 0 1vw; border-radius: 1.4vh; background: var(--tv-panel); border-left: 0.6vw solid transparent;
  font-size: min(4vh, 2.3vw); transition: background 0.6s ease; }
.jqtv-row.top { border-left-color: var(--tv-accent); }
.jqtv-row.top-1 { background: color-mix(in srgb, var(--tv-accent) 18%, var(--tv-panel)); }
.jqtv-rank { text-align: center; font-weight: 900; font-size: 1.15em; color: var(--tv-muted); font-variant-numeric: tabular-nums; }
.jqtv-row.top .jqtv-rank { font-size: 1.45em; color: var(--tv-text); }
.jqtv-name { font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.jqtv-barwrap { height: 32%; border-radius: 99em; background: rgba(255,255,255,0.08); overflow: hidden; }
.jqtv-bar { height: 100%; border-radius: 99em; background: linear-gradient(90deg, var(--tv-bar), var(--tv-accent2));
  transition: width 1.2s cubic-bezier(.2,.8,.2,1); }
.jqtv-today { text-align: right; color: var(--tv-accent); font-weight: 700; font-size: 0.72em; white-space: nowrap; }
.jqtv-count { text-align: right; font-weight: 900; font-size: 1.3em; font-variant-numeric: tabular-nums; }
.jqtv-row.is-up { animation: jqtv-flash 1.4s ease-in-out 3; }
.jqtv-pop { position: absolute; right: 1.2vw; top: -1.4vh; padding: 0.3vh 0.8vw; border-radius: 99em;
  background: var(--tv-accent); color: #111; font-weight: 900; font-size: 0.6em; animation: jqtv-rise 9s ease-out forwards; }
@keyframes jqtv-flash { 0%, 100% { box-shadow: none; } 50% { box-shadow: 0 0 0 0.4vh var(--tv-accent), 0 0 4vh var(--tv-accent); } }
@keyframes jqtv-rise { 0% { opacity: 0; transform: translateY(1vh); } 8% { opacity: 1; transform: none; } 85% { opacity: 1; } 100% { opacity: 0; } }
.jqtv-split { height: 100%; display: grid; grid-template-columns: minmax(0, 1fr) 24vw; gap: 1.8vw; }
.jqtv-split-main { min-width: 0; height: 100%; display: flex; flex-direction: column; }
.jqtv-split .jqtv-row { font-size: min(4vh, 2vw); }
.jqtv-ready { min-height: 0; display: flex; flex-direction: column; padding: 2.6vh 1.6vw 2vh; border-radius: 1.6vh;
  background: var(--tv-panel); border-top: 0.6vh solid var(--tv-accent2); overflow: hidden; }
.jqtv-ready-eyebrow { font-size: min(2.4vh, 1.3vw); letter-spacing: 0.25em; text-transform: uppercase; font-weight: 800; color: var(--tv-accent2); }
.jqtv-ready-num { font-size: min(12vh, 5.4vw); font-weight: 900; line-height: 1.05; font-variant-numeric: tabular-nums; margin-top: 1vh; white-space: nowrap; }
.jqtv-ready-sub { font-size: min(2.8vh, 1.5vw); color: var(--tv-muted); font-weight: 600; }
.jqtv-ready-models { margin-top: 2.6vh; display: flex; flex-direction: column; gap: 1.1vh; }
.jqtv-ready-model { display: flex; justify-content: space-between; gap: 1vw; padding-bottom: 0.9vh;
  border-bottom: 0.2vh solid var(--tv-line); font-size: min(3vh, 1.6vw); }
.jqtv-ready-model span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--tv-text); }
.jqtv-ready-model strong { font-variant-numeric: tabular-nums; }
.jqtv-empty { margin: auto; text-align: center; color: var(--tv-muted); font-size: min(3.4vh, 2vw); }
.jqtv-empty-big { color: var(--tv-text); font-size: min(6vh, 3.4vw); font-weight: 800; margin-bottom: 1.5vh; }
.jqtv-msg { margin: auto; text-align: center; max-width: 80vw; }
.jqtv-msg-emoji { font-size: 22vh; line-height: 1.1; }
.jqtv-msg-body { font-size: min(6vh, 3.4vw); font-weight: 700; line-height: 1.3; white-space: pre-line; }
.jqtv-foot { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 2vw;
  padding-top: 1.4vh; border-top: 0.25vh solid var(--tv-line); color: var(--tv-muted); font-size: min(2.6vh, 1.5vw); }
.jqtv-foot strong { color: var(--tv-text); }
.jqtv-accent { color: var(--tv-accent) !important; }
.jqtv-foot-right { text-align: right; }
.jqtv-offline { color: #fca5a5; font-weight: 700; }
.jqtv-dots { display: flex; gap: 0.8vw; }
.jqtv-dots span { width: 1.2vh; height: 1.2vh; border-radius: 50%; background: var(--tv-line); }
.jqtv-dots span.on { background: var(--tv-accent); }
`;
